/**
 * Phase 4 base FE (AC 4.1) — folder-scoped Raise Query dialog.
 *
 * Opens from the eISF folder tree's "+Query" action. Pre-fills
 * folder_id and section_ref (which the BE stores on the query row) so
 * the monitor doesn't have to re-type context. Study id comes from the
 * binder context (passed by the parent), site id is inferred by the BE
 * from the auth claims.
 *
 * Category picker uses Jawahar's authoritative enum; description body
 * is required.
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MessageSquarePlus, X } from 'lucide-react';
import {
  compliance,
  type BinderFolderRow,
  type QueryCategory,
  type QueryVisibilityType,
} from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { errorText } from '@/lib/permissionError';

const EXTERNAL_RAISER_ROLES = new Set(['monitor', 'auditor']);

const CATEGORY_LABELS: Record<QueryCategory, string> = {
  missing_document: 'Missing document',
  incorrect_metadata: 'Incorrect metadata',
  expired_document: 'Expired document',
  compliance_flag: 'Compliance flag',
  other: 'Other',
};

/**
 * Punch-list f3 — when opened from the document viewer we only have the
 * document's folder_id + breadcrumb on hand, not a full folder row (that
 * would require an extra fetch just for this dialog's labeling). Accept
 * the minimal subset the labels actually use; the folder tree's full
 * `BinderFolderRow` satisfies this shape as-is.
 */
type QueryFolderContext = Pick<
  BinderFolderRow,
  'folder_id' | 'section_name' | 'zone_name' | 'display_name_override' | 'subartifact_label' | 'artifact_type'
>;

export function RaiseFolderQueryDialog({
  folder,
  studyId,
  documentId,
  onClose,
}: {
  folder: QueryFolderContext;
  studyId: string;
  /** Punch-list f3 — set when opened from a specific document (e.g. the
   *  document viewer), so the query stays linked back to that document,
   *  not just the folder it lives in. */
  documentId?: string;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<QueryCategory>('missing_document');
  // Defaults to how the server would derive it for this caller's role
  // (deriveOrigin), but is an explicit, overridable choice here rather
  // than a silent guess.
  const [visibility, setVisibility] = useState<QueryVisibilityType>(
    user && EXTERNAL_RAISER_ROLES.has(user.role) ? 'external' : 'internal',
  );
  const isExternalRaiser = !!user && EXTERNAL_RAISER_ROLES.has(user.role);
  const visibilityMismatch =
    (isExternalRaiser && visibility === 'internal') ||
    (!isExternalRaiser && visibility === 'external');

  const sectionRef = folder.section_name || folder.zone_name || '';
  const folderLabel =
    folder.display_name_override ||
    folder.subartifact_label ||
    folder.artifact_type ||
    'Folder';

  const raise = useMutation({
    mutationFn: () =>
      compliance.raiseQuery({
        study_id: studyId,
        body: body.trim(),
        category,
        folder_id: folder.folder_id,
        section_ref: sectionRef,
        document_id: documentId,
        origin: visibility === 'external' ? 'cra_query' : 'internal',
      }),
    onSuccess: (r) => {
      toast.success(`Query ${r.query_id.slice(0, 8)}… raised on this section.`);
      qc.invalidateQueries({ queryKey: ['compliance-queries'] });
      onClose();
    },
    onError: (err) =>
      toast.error(errorText(err, 'Failed to raise query')),
  });

  return createPortal(
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
      aria-labelledby="raise-folder-query-title"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <div className="min-w-0">
            <h3
              id="raise-folder-query-title"
              className="flex items-center gap-2 text-sm font-semibold text-ink-primary"
            >
              <MessageSquarePlus className="h-4 w-4 text-accent-700" />
              Raise a query
            </h3>
            <p className="mt-0.5 truncate text-[11px] text-ink-muted">
              Section <span className="font-mono">{sectionRef}</span> · {folderLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-3 px-4 py-3">
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as QueryCategory)}
              className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
            >
              {(Object.keys(CATEGORY_LABELS) as QueryCategory[]).map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          </label>

          <fieldset className="text-xs">
            <legend className="font-medium text-ink-secondary">Query type</legend>
            <div className="mt-1 flex gap-4">
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="folder-query-visibility"
                  checked={visibility === 'internal'}
                  onChange={() => setVisibility('internal')}
                />
                Internal
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="folder-query-visibility"
                  checked={visibility === 'external'}
                  onChange={() => setVisibility('external')}
                />
                External
              </label>
            </div>
            {visibilityMismatch && (
              <p className="mt-1.5 rounded border border-danger-200 bg-danger-50 px-2 py-1.5 text-[11px] text-danger-800">
                {isExternalRaiser
                  ? 'Monitors/auditors can only raise External queries.'
                  : 'Site staff can only raise Internal queries.'}
              </p>
            )}
          </fieldset>

          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Description</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Describe the issue on this section…"
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
            <div className="mt-0.5 text-right text-[10px] text-ink-muted">
              {body.length} / 2000
            </div>
          </label>

          <p className="rounded border border-surface-border bg-surface-muted/30 px-3 py-2 text-[11px] text-ink-muted">
            The query will be filed against this section with your monitor account. Site staff can
            respond; when it's answered, a monitor or site admin can close it.
          </p>
        </div>

        <footer className="flex justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => raise.mutate()}
            disabled={!body.trim() || raise.isPending || visibilityMismatch}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {raise.isPending ? 'Raising…' : 'Raise query'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
